import { aiPhotoGenerationPaidLimit } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoGeneration';
import {
    getAiPhotoGenerationAttemptsCounter,
    getAiPhotoGenerationLastAttemptTime,
    incrementAiPhotoGenerationAttemptsCounter,
    updateAiPhotoGenerationFlag,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.flags';

const DAY_IN_SECONDS = 24 * 60 * 60;

const getNowInSeconds = () => Math.floor(Date.now() / 1000);

/**
 * Окно лимита — скользящие 24ч от времени первой генерации (lastAttemptTime).
 * Сброс ленивый: счётчик обнуляется не по таймеру, а на следующей генерации
 * после истечения окна. Совместимо с реализацией мобильного приложения,
 * хранящего состояние в том же почтовом хелпере (time + count.show).
 */
const isLimitWindowActive = (lastAttemptTime: number): boolean => lastAttemptTime + DAY_IN_SECONDS > getNowInSeconds();

const canMakeRequest = async (limit: number): Promise<boolean> => {
    const [lastAttemptTime, attemptsCount] = await Promise.all([
        getAiPhotoGenerationLastAttemptTime(),
        getAiPhotoGenerationAttemptsCounter(),
    ]);

    // Окно истекло — доступна полная квота
    if (!isLimitWindowActive(lastAttemptTime)) {
        return true;
    }

    // Активное окно означает, что минимум одна генерация уже сделана, поэтому
    // отсутствующий (undefined) счётчик трактуем как 1, а не как полную квоту.
    return (attemptsCount || 1) < limit;
};

// Открываем новое окно лимита
const resetAiPhotoGenerationAttemptsCounter = () => updateAiPhotoGenerationFlag({ time: true, show: 1 });

const addRequest = async (): Promise<void> => {
    const lastAttemptTime = await getAiPhotoGenerationLastAttemptTime();

    // Окно активно — продолжаем его, инкрементируя счётчик (time не трогаем)
    if (isLimitWindowActive(lastAttemptTime)) {
        await incrementAiPhotoGenerationAttemptsCounter();
        return;
    }

    // Окно истекло — открываем новое
    await resetAiPhotoGenerationAttemptsCounter();
};

const isDifferentDay = (a: Date, b: Date): boolean =>
    a.getFullYear() !== b.getFullYear() || a.getMonth() !== b.getMonth() || a.getDate() !== b.getDate();

const getNextTime = async (): Promise<{ time: string | null; isTomorrow: boolean }> => {
    const lastAttemptTime = await getAiPhotoGenerationLastAttemptTime();

    if (!lastAttemptTime) {
        return { time: null, isTomorrow: false };
    }

    const nextDate = new Date((lastAttemptTime + DAY_IN_SECONDS) * 1000);

    return {
        time: nextDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        }),
        isTomorrow: isDifferentDay(nextDate, new Date()),
    };
};

const getAttemptsLeft = async () => {
    const [lastAttemptTime, attemptsCount] = await Promise.all([
        getAiPhotoGenerationLastAttemptTime(),
        getAiPhotoGenerationAttemptsCounter(),
    ]);

    if (!lastAttemptTime) {
        return aiPhotoGenerationPaidLimit;
    }

    return aiPhotoGenerationPaidLimit - attemptsCount;
};

const aiPhotoGenerationClamp = {
    canMakeRequest,
    addRequest,
    getNextTime,
    getAttemptsLeft,
};

export default aiPhotoGenerationClamp;
